/* eslint-disable @typescript-eslint/no-explicit-any */
import { Badge, Box, Checkbox, HStack, VStack } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { Text } from '../../../../components/atoms/Text';
import { Button } from '../../../../components/atoms/Button';
import { useTheme } from '../../../../contexts/ThemeProvider';
import { masks } from '../../../../utils/masks';

interface PatientOption {
  value: string;
  label: string;
  name: string;
  cpf: string;
}

interface PatientAccessStepProps {
  isLoadingBenefits: boolean;
  hasTelemedicineProduct: boolean;
  hasTelemedicineSubscription: boolean;
  isLegacyTelemedicineAccess: boolean;
  planName: string;
  patientOptions: PatientOption[];
  selectedPatient: string;
  onSelectPatient: (value: string) => void;
  profile: any;
  onContactProvider: () => void;
  onConfirmed: () => void;
}

export function PatientAccessStep({
  isLoadingBenefits,
  hasTelemedicineProduct,
  hasTelemedicineSubscription,
  isLegacyTelemedicineAccess,
  planName,
  patientOptions,
  selectedPatient,
  onSelectPatient,
  profile,
  onContactProvider,
  onConfirmed,
}: PatientAccessStepProps) {
  const { colors } = useTheme();
  const [confirmedData, setConfirmedData] = useState(false);

  useEffect(() => {
    setConfirmedData(false);
  }, [selectedPatient]);

  if (isLoadingBenefits) {
    return (
      <Box width={'100%'} padding={'20px'} borderRadius={'16px'} backgroundColor={'#F7F9FA'}>
        <VStack align={'start'} spacing={2}>
          <Badge colorScheme="blue" borderRadius={'full'} paddingX={3} paddingY={1}>
            Validando acesso
          </Badge>
          <Text fontSize={'18px'} fontWeight={700}>
            Estamos conferindo seus produtos e assinaturas.
          </Text>
          <Text fontSize={'14px'} color={'#4A5568'}>
            Em instantes vamos liberar o próximo passo do agendamento.
          </Text>
        </VStack>
      </Box>
    );
  }

  if (!hasTelemedicineProduct) {
    return (
      <Box
        width={'100%'}
        padding={'20px'}
        borderRadius={'16px'}
        background={'linear-gradient(135deg, #FFF7ED 0%, #FFFBEB 100%)'}
        border={'1px solid #FED7AA'}
      >
        <VStack align={'start'} spacing={2}>
          <Badge colorScheme="orange" borderRadius={'full'} paddingX={3} paddingY={1}>
            Produto indisponível
          </Badge>
          <Text fontSize={'18px'} fontWeight={700}>
            Telemedicina ainda não está liberada para este convênio.
          </Text>
          <Text fontSize={'14px'} color={'#4A5568'}>
            Assim que o produto estiver disponível, o agendamento aparecerá automaticamente nesta página.
          </Text>
        </VStack>
      </Box>
    );
  }

  if (!hasTelemedicineSubscription) {
    return (
      <Box width={'100%'} padding={'20px'} borderRadius={'16px'} backgroundColor={'#F7F9FA'}>
        <VStack align={'start'} spacing={4}>
          <Badge colorScheme="yellow" borderRadius={'full'} paddingX={3} paddingY={1}>
            Assinatura pendente
          </Badge>
          <Text fontSize={'18px'} fontWeight={700}>
            Não encontramos uma subscrição ativa para Telemedicina.
          </Text>
          <Text fontSize={'14px'} color={'#4A5568'}>
            Fale com o provedor para conferir sua assinatura deste serviço e liberar o agendamento.
          </Text>
          <Button onClick={onContactProvider}>Contactar provedor pelo WhatsApp</Button>
        </VStack>
      </Box>
    );
  }

  return (
    <VStack width={'100%'} spacing={5} alignItems={'start'}>
      <Box width={'100%'} padding={'20px'} borderRadius={'16px'} backgroundColor={'#F0FFF4'}>
        <VStack align={'start'} spacing={2}>
          <Badge colorScheme="green" borderRadius={'full'} paddingX={3} paddingY={1}>
            {isLegacyTelemedicineAccess ? 'Agendamento disponível' : 'Assinatura ativa'}
          </Badge>
          <Text fontSize={'15px'} fontWeight={700} color={'#2F855A'}>
            {isLegacyTelemedicineAccess
              ? 'Boas-vindas! Confirme seus dados para seguir com o agendamento.'
              : `Boas-vindas! Parabéns, seu plano ${planName} está pronto para uso.`}
          </Text>
        </VStack>
      </Box>

      <Box width={'100%'} padding={'20px'} borderRadius={'16px'} backgroundColor={'#FFFFFF'} border={'1px solid #E2E8F0'}>
        <VStack align={'start'} spacing={4}>
          <Box>
            <Text fontSize={'18px'} fontWeight={700}>
              Confirme os dados do atendimento
            </Text>
            <Text fontSize={'14px'} color={'#4A5568'}>
              Estes dados serão enviados para registro do atendimento. Depois, valide o documento com foto do CPF
              antes de abrir o agendamento.
            </Text>
          </Box>

          <Box width={'100%'}>
            <Text fontSize={'14px'} fontWeight={600} marginBottom={2}>
              Paciente
            </Text>
            <VStack width={'100%'} align={'stretch'} spacing={2}>
              {patientOptions.map((option) => {
                const isSelected = option.value === selectedPatient;

                return (
                  <Box
                    key={option.value}
                    role="button"
                    tabIndex={0}
                    cursor={'pointer'}
                    padding={'12px'}
                    borderRadius={'14px'}
                    border={'1px solid'}
                    borderColor={isSelected ? colors.primaryColor : '#E2E8F0'}
                    backgroundColor={isSelected ? '#F0FFF4' : '#FAFAF9'}
                    onClick={() => onSelectPatient(option.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') onSelectPatient(option.value);
                    }}
                  >
                    <HStack justify={'space-between'} align={'flex-start'} spacing={3}>
                      <Box flex={1} minWidth={0}>
                        <Text fontSize={'14px'} fontWeight={700} wordBreak={'break-word'}>
                          {option.label}
                        </Text>
                        <Text fontSize={'12px'} color={'#4A5568'} wordBreak={'break-word'}>
                          CPF: {option.cpf}
                        </Text>
                        {option.value === 'holder' && (
                          <Text fontSize={'12px'} color={'#4A5568'} wordBreak={'break-word'}>
                            {profile?.email || 'E-mail não informado'} ·{' '}
                            {profile?.telephone ? masks.cell(profile.telephone) : 'WhatsApp não informado'}
                          </Text>
                        )}
                      </Box>
                      {isSelected && (
                        <Badge colorScheme="green" borderRadius={'full'} paddingX={2} flexShrink={0}>
                          Selecionado
                        </Badge>
                      )}
                    </HStack>
                  </Box>
                );
              })}
            </VStack>
          </Box>

          <Checkbox isChecked={confirmedData} onChange={(event) => setConfirmedData(event.target.checked)} colorScheme={'green'}>
            <Text fontSize={'14px'}>
              Eu confirmo que os dados acima estão corretos e autorizo a conferência no atendimento.
            </Text>
          </Checkbox>

          <Button onClick={onConfirmed} isDisabled={!confirmedData}>
            Confirmar dados e validar documento
          </Button>
        </VStack>
      </Box>
    </VStack>
  );
}
